import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import { PanelTop, Pencil } from 'lucide-react';
import IconArrowDown from '@/assets/icons/icon-arrow-down-01.svg?react';
import IconDelete from '@/assets/icons/icon-delete.svg?react';
import IconPlus from '@/assets/icons/icon-plus-sign.svg?react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { PrimaryButton } from '@/components/buttons/PrimaryButton/PrimaryButton';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import { useImageManager } from '@/hooks/useImageManager';
import type { AssetResponse } from '@lp/lib-asset-api';
import type { FaviconSettings } from '@builder4/shared/types/settings';
import { selectFaviconSettings } from '@/store/settings/favicon/selectors';
import { selectCurrentPageId } from '@/store/pages/selectors';
import { useAppSelector } from '@/store/hooks';
import { shallowEqual } from '@/utils/settingsHelpers';
import { useSettingsDebounceUpdate } from '@/hooks/useSettingsDebounceUpdate';

interface SettingsFaviconProps {
  className: string;
  onClick?: () => void;
  isOpenCollapse: boolean;
}

const ALLOWED_FAVICON_EXTENSIONS = ['.png', '.svg', '.ico'];
const ALLOWED_FAVICON_CONTENT_TYPES = [
  'image/png',
  'image/svg+xml',
  'image/x-icon',
  'image/vnd.microsoft.icon',
];

const validateFaviconImage = (image: AssetResponse): string | null => {
  const filename = (image.filename ?? '').toLowerCase();
  const contentType = (image.contentType ?? '').toLowerCase();
  const hasValidExt = ALLOWED_FAVICON_EXTENSIONS.some((ext) => filename.endsWith(ext));
  const hasValidType = ALLOWED_FAVICON_CONTENT_TYPES.includes(contentType);
  if (!hasValidExt && !hasValidType) return 'Favicon should be a PNG, SVG, or ICO file.';

  const width = image.width ?? image.metadata?.width;
  const height = image.height ?? image.metadata?.height;
  if (width && height) {
    if (width !== height) return 'Favicon should be a square image.';
    if (width < 48) return 'Favicon should be at least 48x48px.';
  }

  return null;
};

const defaultSettings: FaviconSettings = {
  faviconUrl: null,
};

export const SettingsFavicon: React.FC<SettingsFaviconProps> = ({
  className = '',
  onClick,
  isOpenCollapse,
}) => {
  const faviconSettingsRedux = useAppSelector(selectFaviconSettings) ?? defaultSettings;
  const currentPageId = useAppSelector(selectCurrentPageId);
  const [isOpen] = useState(isOpenCollapse);
  const [faviconSettings, setFaviconSettings] = useState<FaviconSettings>(faviconSettingsRedux);
  const [validationWarning, setValidationWarning] = useState<string | null>(null);
  const { openImageManager } = useImageManager();

  const handleClick = () => {
    if (onClick) onClick();
  };

  const handleImageSelect = (image: AssetResponse) => {
    setValidationWarning(validateFaviconImage(image));
    setFaviconSettings({ faviconUrl: image.fileUri });
  };

  const handleImageRemove = () => {
    setValidationWarning(null);
    setFaviconSettings({ faviconUrl: null });
  };

  const handleChooseClick = () => {
    openImageManager({
      onSelectImage: handleImageSelect,
      allowedContentTypes: ALLOWED_FAVICON_CONTENT_TYPES,
    });
  };

  const handleReplaceClick = () => {
    openImageManager({
      onSelectImage: handleImageSelect,
      allowedContentTypes: ALLOWED_FAVICON_CONTENT_TYPES,
    });
  };

  useSettingsDebounceUpdate({
    value: faviconSettings,
    bodyKey: 'favicon_settings',
    currentPageId: currentPageId,
    reduxValue: faviconSettingsRedux,
  });

  useEffect(() => {
    if (!faviconSettingsRedux) return;

    setFaviconSettings((prev) => {
      if (shallowEqual(prev, faviconSettingsRedux)) {
        return prev;
      }
      return faviconSettingsRedux;
    });
  }, [faviconSettingsRedux]);

  return (
    <Collapsible defaultOpen={isOpen}>
      <CollapsibleTrigger
        onClick={handleClick}
        data-qa-selector="settings-favicon-toggle"
        className={clsx(
          'group bg-neutral-0 w-full cursor-pointer border-t border-neutral-100 px-2 py-4',
          className,
        )}
      >
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <PanelTop className="h-4 w-4 text-neutral-900" />
            <span className="text-xs text-neutral-900">Favicon</span>
          </div>
          <IconArrowDown className="h-4 w-4 text-neutral-500 transition-all group-aria-expanded:rotate-180" />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent className={clsx('bg-neutral-0 max-w-[232px] pb-3')}>
        <div className="flex flex-col gap-2 p-2">
          <div
            className={clsx(
              'group bg-neutral-10 relative inline-flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-2.5 rounded outline outline-offset-[-1px] outline-indigo-500 outline-dashed',
            )}
          >
            {faviconSettings.faviconUrl ? (
              <img
                src={faviconSettings.faviconUrl}
                alt="Favicon preview"
                className="h-full w-full rounded object-contain p-1"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center rounded bg-indigo-50">
                <PanelTop className="h-6 w-6 text-indigo-300" />
              </div>
            )}

            {!faviconSettings.faviconUrl && (
              <div className="bg-neutral-10 absolute top-0 left-0 z-10 flex h-full w-full items-center justify-center rounded opacity-0 transition-all group-hover:opacity-100">
                <PrimaryButton
                  size="sm"
                  text="Choose"
                  icon={IconPlus}
                  onClick={handleChooseClick}
                />
              </div>
            )}

            {faviconSettings.faviconUrl && (
              <div className="absolute top-0 left-0 z-10 flex h-full w-full items-center justify-center gap-1 rounded bg-indigo-50/80 opacity-0 transition-all group-hover:opacity-100">
                <PrimaryButton text="" size="sm" icon={Pencil} onClick={handleReplaceClick} />
                <SecondaryButton text="" size="sm" icon={IconDelete} onClick={handleImageRemove} />
              </div>
            )}
          </div>

          {validationWarning && (
            <p className="text-[10px] font-medium text-amber-500">{validationWarning}</p>
          )}

          <p className="text-[10px] font-medium text-neutral-400">
            Recommended: square PNG, SVG, or ICO, 48×48px minimum.
          </p>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
